<template>
  <div class="call-fb-header-container">
    <div class="select-user">
      <SelectUser
        v-model="selectedCallerId"
        :use-all-label="i18n.t('callResult.allCallers')"
        only-active-users
        flexible-width
        :show-overlay-loading-indicator="false"
      />
    </div>
    <BDateRangeForm
      :model-value="dateRageFormValue"
      type="daterange"
      :max-months-range="3"
      :clearable="false"
      @update:model-value="handleDateRangeUpdate"
    />
    <div class="call-connection-point-select">
      <CallConnectionPointSelect
        v-model="selectedCallConnectionPoint"
      />
    </div>
    <div class="call-result-type-select">
      <CallResultTypeSelect
        v-model:default-call-result-type-id="selectedDefaultCallResultTypeId"
        v-model:custom-call-result-type-id="selectedCustomCallResultTypeId"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineModel, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import SelectUser from '@/plugins/biscuet-materials/components/molecules/SelectUser.vue';
import { startOf, subtract, formatISO8601Date } from '@/utils/date-time';
import CallConnectionPointSelect from './CallConnectionPointSelect.vue';
import CallResultTypeSelect from './CallResultTypeSelect.vue';

const i18n = useI18n();

const selectedCallerId = defineModel<number | undefined>('callerId');
const selectedStartDate = defineModel<string | Date | null>('startDate', {
  default: null,
  get(value: string | null) {
    if (value == null) return null;
    return new Date(value);
  },
  set(value: Date | null) {
    if (value == null) return null;
    return formatISO8601Date(value);
  },
});
const selectedEndDate = defineModel<string | Date | null>('endDate', { default: null,
  get(value: string | null) {
    if (value == null) return null;
    return new Date(value);
  },
  set(value: Date | null) {
    if (value == null) return null;
    return formatISO8601Date(value);
  },
});
const selectedDefaultCallResultTypeId = defineModel<number | null>('defaultCallResultTypeId', { default: null });
const selectedCustomCallResultTypeId = defineModel<number | null>('customCallResultTypeId', { default: null });
const selectedCallConnectionPoint = defineModel<string | null>('callConnectionPoint', { default: null });

const dateRageFormValue = computed<Date[]>(() => {
  return [selectedStartDate.value as Date, selectedEndDate.value as Date];
});

const handleDateRangeUpdate = (dateRange: Date[]) => {
  selectedStartDate.value = dateRange[0];
  selectedEndDate.value = dateRange[1];
};

onMounted(() => {
  selectedStartDate.value ??= subtract(startOf(new Date(), 'day'), 14, 'day');
  selectedEndDate.value ??= startOf(new Date(), 'day');
});
</script>

<style lang="scss" scoped>
.call-fb-header-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  background-color: $bgcolor-base;
  padding: $basespace-200 $basespace-500;
  gap: $basespace-50;
}

.select-user {
  width: 240px;
}

.call-connection-point-select {
  width: 160px;
}

.call-result-type-select {
  width: 240px;
}
</style>
